import { useState } from 'react';
import type { LineContactListItem } from '@app/shared';
import { useLineContacts, type ContactListFilter } from './lineApi';

interface Props {
  selectedId: number | null;
  onSelect: (contact: LineContactListItem) => void;
}

const FILTERS: { value: ContactListFilter; label: string }[] = [
  { value: 'ALL', label: '全部' },
  { value: 'AI', label: 'AI' },
  { value: 'HUMAN', label: '人工' },
  { value: 'CLOSED', label: '已關閉' },
];

export function ContactList({ selectedId, onSelect }: Props): JSX.Element {
  const [filter, setFilter] = useState<ContactListFilter>('ALL');
  const { data, isLoading, isError, error } = useLineContacts(filter);

  return (
    <aside className="flex flex-col border-r bg-white min-h-0">
      <div className="flex border-b">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={
              'flex-1 px-2 py-2 text-xs ' +
              (filter === f.value
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600 font-medium'
                : 'text-slate-600 hover:bg-slate-50')
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {isLoading ? (
          <div className="p-3 text-sm text-slate-500">載入中...</div>
        ) : isError ? (
          <div role="alert" className="p-3 text-sm text-red-600">
            載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="p-3 text-sm text-slate-500">沒有對話</div>
        ) : (
          data.items.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c)}
              className={
                'w-full text-left px-3 py-2 border-b hover:bg-slate-50 ' +
                (selectedId === c.id ? 'bg-blue-50' : '')
              }
            >
              <div className="flex items-center gap-2">
                {c.pictureUrl ? (
                  <img
                    src={c.pictureUrl}
                    alt=""
                    className="w-8 h-8 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs text-slate-500">
                    {(c.displayName ?? '?').slice(0, 1)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">
                      {c.displayName ?? c.lineUserId}
                    </span>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500 truncate">
                      {c.lastMessagePreview ?? '（無訊息）'}
                    </span>
                    {c.unreadCount > 0 ? (
                      <span className="text-[10px] bg-red-500 text-white rounded-full px-1.5 py-0.5">
                        {c.unreadCount}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>
    </aside>
  );
}

function StatusBadge({ status }: { status: 'AI' | 'HUMAN' | 'CLOSED' }): JSX.Element {
  const cls =
    status === 'AI'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'HUMAN'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-200 text-slate-600';
  const label = status === 'AI' ? 'AI' : status === 'HUMAN' ? '人工' : '已關閉';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls} shrink-0`}>{label}</span>
  );
}
